import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { ProductDetail as ProductDetailType } from '../types';
import { ApiService } from '../utils/api';
import { ArrowLeft, ExternalLink, Star, Clock, Shield, TrendingDown, TrendingUp, Tag } from 'lucide-react';

const ProductDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const [productDetail, setProductDetail] = useState<ProductDetailType | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (id) {
      fetchProductDetail();
    }
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchProductDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await ApiService.getProduct(id!);
      setProductDetail(data);
    } catch (err) {
      setError('Failed to load product details. Please try again.');
      console.error('Error fetching product detail:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleVendorClick = async (listing: any) => {
    await ApiService.logClick(listing.id, listing.product_id, listing.vendor_id);
    window.open(listing.url, '_blank');
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading product details...</p>
        </div>
      </div>
    );
  }

  if (error || !productDetail) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error}</p>
          <button
            onClick={fetchProductDetail}
            className="px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  const { product, listings, value_brief } = productDetail;
  const sortedListings = listings.sort((a, b) => (a.price || Infinity) - (b.price || Infinity));
  const bestPrice = sortedListings.find(l => l.price)?.price;

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Breadcrumb */}
      <div className="bg-white border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <Link
            to={`/category/${product.category.toLowerCase().replace(/[^a-z]/g, '_')}`}
            className="inline-flex items-center text-primary-600 hover:text-primary-700 transition-colors"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to {ApiService.getCategoryDisplayName(product.category)}
          </Link>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="lg:grid lg:grid-cols-3 lg:gap-8">
          {/* Left Column - Product Info & Listings */}
          <div className="lg:col-span-2">
            {/* Product Header with Image */}
            <div className="bg-white rounded-xl shadow-lg p-6 mb-6">
              <div className="flex flex-col lg:flex-row lg:items-start gap-6 mb-4">
                {/* Product Image */}
                <div className="flex-shrink-0 lg:w-64">
                  {product.image_url && !product.image_url.includes('placeholder') ? (
                    <img 
                      src={product.image_url} 
                      alt={product.canonical_name}
                      className="w-full h-48 lg:h-56 object-cover rounded-lg border border-gray-200"
                      onError={(e) => {
                        e.currentTarget.style.display = 'none';
                        const fallback = e.currentTarget.nextElementSibling as HTMLElement;
                        if (fallback) fallback.style.display = 'flex';
                      }}
                    />
                  ) : null}
                  <div className={`w-full h-48 lg:h-56 bg-gradient-to-br from-gray-100 to-gray-200 rounded-lg border border-gray-200 flex items-center justify-center ${product.image_url && !product.image_url.includes('placeholder') ? 'hidden' : ''}`}>
                    <div className="text-center">
                      <div className="w-16 h-16 bg-primary-100 rounded-full flex items-center justify-center mx-auto mb-3">
                        <Tag className="w-8 h-8 text-primary-600" />
                      </div>
                      <p className="text-gray-500 text-sm font-medium">{product.brand}</p>
                    </div>
                  </div>
                </div>
                
                {/* Product Info */}
                <div className="flex-1">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">{product.canonical_name}</h1>
                  <p className="text-lg text-gray-600">{product.brand}</p>
                  <span className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-primary-50 text-primary-700 border border-primary-200 mt-2">
                    {ApiService.getCategoryDisplayName(product.category)}
                  </span>
                </div>
                {bestPrice && (
                  <div className="text-right">
                    <div className="text-3xl font-bold text-green-600">
                      {ApiService.formatPrice(bestPrice)}
                    </div>
                    <p className="text-sm text-gray-500">Best Price</p>
                  </div>
                )}
              </div>
            </div>

            {/* Vendor Listings */}
            <div className="bg-white rounded-xl shadow-lg p-6">
              <h2 className="text-2xl font-bold text-gray-900 mb-6">Available at</h2>
              
              {listings.length === 0 ? (
                <div className="text-center py-8">
                  <p className="text-gray-500">No vendor listings available at the moment.</p>
                  <p className="text-gray-400 text-sm mt-1">Check back soon for updates!</p>
                </div>
              ) : (
                <div className="space-y-4">
                  {sortedListings.map((listing, index) => (
                    <div
                      key={listing.id}
                      className="border border-gray-200 rounded-lg p-4 hover:border-primary-300 hover:shadow-md transition-all duration-200 cursor-pointer"
                      onClick={() => handleVendorClick(listing)}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex-1">
                          <div className="flex items-center space-x-3">
                            <h3 className="font-semibold text-gray-900">{listing.vendor_name}</h3>
                            {index === 0 && listing.price && (
                              <span className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800">
                                Best Price
                              </span>
                            )}
                          </div>
                          <p className="text-sm text-gray-500 mt-1">{listing.vendor_domain}</p>
                          
                          <div className="flex items-center space-x-4 mt-2">
                            <div className="flex items-center text-sm text-gray-600">
                              <Clock className="w-4 h-4 mr-1" />
                              Updated {new Date(listing.last_seen).toLocaleDateString()}
                            </div>
                            
                            {listing.availability && (
                              <div className={`flex items-center text-sm ${
                                listing.availability === 'in_stock' ? 'text-green-600' : 'text-yellow-600'
                              }`}>
                                <Shield className="w-4 h-4 mr-1" />
                                {listing.availability === 'in_stock' ? 'In Stock' : 'Limited Stock'}
                              </div>
                            )}
                          </div>
                        </div>
                        
                        <div className="text-right">
                          {listing.price ? (
                            <div className="text-2xl font-bold text-gray-900">
                              {ApiService.formatPrice(listing.price, listing.currency)}
                            </div>
                          ) : (
                            <div className="text-lg font-medium text-gray-500">
                              Price on request
                            </div>
                          )}
                          
                          <div className="flex items-center mt-2 text-primary-600">
                            <span className="text-sm font-medium mr-1">Visit Store</span>
                            <ExternalLink className="w-4 h-4" />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Right Column - Value Brief */}
          <div className="mt-8 lg:mt-0">
            <div className="bg-white rounded-xl shadow-lg p-6 sticky top-6">
              <h2 className="text-xl font-bold text-gray-900 mb-4 flex items-center">
                <Star className="w-5 h-5 text-yellow-500 mr-2" />
                Expert Review
              </h2>
              
              {value_brief ? (
                <div className="space-y-6">
                  {/* Score */}
                  <div className="text-center">
                    <div className="text-4xl font-bold text-primary-600 mb-2">
                      {value_brief.score}/10
                    </div>
                    <p className="text-gray-600">Overall Rating</p>
                  </div>
                  
                  {/* Audience */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-2">Perfect For</h3>
                    <p className="text-gray-700 text-sm leading-relaxed">{value_brief.audience}</p>
                  </div>
                  
                  {/* Pros */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <TrendingUp className="w-4 h-4 text-green-500 mr-2" />
                      Pros
                    </h3>
                    <ul className="space-y-2">
                      {value_brief.pros.map((pro, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <div className="w-2 h-2 bg-green-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {pro}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {/* Cons */}
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3 flex items-center">
                      <TrendingDown className="w-4 h-4 text-red-500 mr-2" />
                      Cons
                    </h3>
                    <ul className="space-y-2">
                      {value_brief.cons.map((con, index) => (
                        <li key={index} className="flex items-start text-sm text-gray-700">
                          <div className="w-2 h-2 bg-red-500 rounded-full mt-2 mr-3 flex-shrink-0"></div>
                          {con}
                        </li>
                      ))}
                    </ul>
                  </div>
                  
                  {productDetail.value_brief_updated_at && (
                    <div className="pt-4 border-t border-gray-200 text-xs text-gray-500">
                      Last updated: {new Date(productDetail.value_brief_updated_at).toLocaleDateString()}
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8">
                  <Star className="w-12 h-12 text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Expert review coming soon</p>
                  <p className="text-gray-400 text-sm mt-1">Our audio experts are working on this review</p>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductDetail;
